import React from 'react';

import type { BaseButtonSize } from './BaseButton';
import BaseButton from './BaseButton';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'outline'
  | 'text'
  | 'ghost';

export type ButtonSize = BaseButtonSize;

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  isSubmit?: boolean;
}

const Button: React.FC<IButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  isSubmit = false,
  className = '',
  disabled,
  ...props
}) => {
  const getVariantClass = (variant: string): string => {
    const variants: Record<string, string> = {
      primary:
        'bg-theme-500 text-white hover:bg-theme-600 focus-visible:ring-2 focus-visible:ring-theme-500 focus-visible:ring-offset-2 shadow-md',
      secondary:
        'bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
      danger:
        'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
      success:
        'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2',
      warning:
        'bg-yellow-500 text-white hover:bg-yellow-600 focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2',
      outline:
        'border-2 border-theme-500 text-theme-500 hover:bg-theme-50 focus-visible:ring-2 focus-visible:ring-theme-500 focus-visible:ring-offset-2',
      text: 'text-theme-500 hover:text-theme-600 bg-transparent hover:bg-transparent focus-visible:ring-2 focus-visible:ring-theme-500 focus-visible:ring-offset-2',
      ghost:
        'text-gray-500 hover:text-gray-700 bg-transparent hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
    };
    return variants[variant] || variants.primary;
  };

  const getSizeClass = (size: ButtonSize): string => {
    const sizes = {
      sm: 'px-4 py-2 text-sm',
      md: 'px-5 py-2.5 text-base min-w-[100px] min-h-[44px]',
      lg: 'px-7 py-3 text-lg min-w-[120px] min-h-[52px]',
      icon: 'p-2',
    };
    return sizes[size];
  };

  const getBaseClasses = (): string => {
    return 'inline-flex items-center justify-center rounded-xl font-semibold transition-all duration-200 focus:outline-none focus-visible:outline-none transform-gpu active:scale-95';
  };

  const getDisabledClass = (disabled: boolean, isLoading: boolean): string => {
    return disabled || isLoading ? 'opacity-60 cursor-not-allowed  bg-gray-200    shadow-none' : '';
  };

  return (
    <BaseButton
      variant={variant}
      size={size}
      isLoading={isLoading}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      fullWidth={fullWidth}
      isSubmit={isSubmit}
      className={className}
      disabled={disabled}
      getVariantClass={getVariantClass}
      getSizeClass={getSizeClass}
      getBaseClasses={getBaseClasses}
      getDisabledClass={getDisabledClass}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </BaseButton>
  );
};

export default Button;
